/**
 * Frontend service for interacting with the dynamic pricing engine
 * Provides methods to execute pricing, preview changes, and manage rules
 */

import { supabase } from '../../../services/supabaseClient';
import { getWebHotelierPricingBridge } from '../../webhotelier/services/webHotelierPricingBridge';
import { getRealTimePricingService } from '../../../services/realTimePricingService';
import { PRICING_CONFIG } from '../../../config/pricingConfig';
import { assertPropertyAccess } from '../../../services/propertyAuthorization';

export interface PricingPreviewRequest {
  propertyId: string;
  roomTypeId: string;
  date: string;
  occupancy: number;
}

export interface PricingPreviewResponse {
  currentPrice: number;
  suggestedPrice: number;
  appliedRules: string[];
  priceChange: number;
  priceChangePercentage: number;
}

export interface PricingExecutionResult {
  success: boolean;
  propertiesProcessed: number;
  pricesUpdated: number;
  errors: string[];
}

import { PricingRule } from '../types/revenue';

export interface PricingRuleEvaluation {
  rule_id: string;
  rule_name: string;
  applied: boolean;
  adjustments: {
    type: string;
    value: number;
    description: string;
  }[];
}

export interface RoomTypeLimit {
  id: string;
  property_id: string;
  room_type_id: string;
  min_price: number;
  max_price: number;
  currency: string;
}

export interface PriceLog {
  id: string;
  property_id: string;
  room_type_id: string;
  date: string;
  old_price: number;
  new_price: number;
  rule_triggered: string;
  change_reason: string;
  occupancy_at_change: number;
  booking_window_days: number;
  timestamp: string;
  created_by: string;
}

export class PricingEngineService {
  private supabaseUrl: string;
  private supabaseKey: string;
  private pricingBridge: ReturnType<typeof getWebHotelierPricingBridge> | null =
    null;
  private realTimePricingService: ReturnType<
    typeof getRealTimePricingService
  > | null = null;

  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    this.supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';
  }

  // Edge functions authenticate via the user's session JWT (see
  // supabase/functions/_shared/propertyAuthorization.ts). The publishable key
  // is NOT a JWT and is rejected by getUser() with "Invalid or expired session".
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      throw new Error(
        'No active session — please sign in again to apply pricing rules'
      );
    }
    return {
      Authorization: `Bearer ${accessToken}`,
      apikey: this.supabaseKey,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get pricing bridge with lazy initialization
   */
  private getPricingBridge() {
    if (!this.pricingBridge) {
      try {
        console.log('[PricingEngineService] Lazy loading pricing bridge...');
        this.pricingBridge = getWebHotelierPricingBridge();
      } catch (error) {
        console.error(
          '[PricingEngineService] Failed to get pricing bridge:',
          error
        );
        this.pricingBridge = null;
      }
    }
    return this.pricingBridge;
  }

  /**
   * Get real-time pricing service with lazy initialization
   */
  private getRealTimePricingService() {
    if (!this.realTimePricingService) {
      try {
        console.log(
          '[PricingEngineService] Lazy loading real-time pricing service...'
        );
        this.realTimePricingService = getRealTimePricingService();
      } catch (error) {
        console.error(
          '[PricingEngineService] Failed to get real-time pricing service:',
          error
        );
        this.realTimePricingService = null;
      }
    }
    return this.realTimePricingService;
  }

  /**
   * Execute the dynamic pricing engine for all properties or specific ones
   * Enhanced with WebHotelier integration and real-time pricing
   */
  async executePricingEngine(
    propertyIds?: string[]
  ): Promise<PricingExecutionResult> {
    try {
      // If property IDs specified, use WebHotelier-integrated approach
      if (propertyIds && propertyIds.length > 0) {
        for (const id of propertyIds) {
          await assertPropertyAccess(id);
        }
        return await this.executeWebHotelierIntegratedPricing(propertyIds);
      }

      // Fallback to legacy Supabase function approach
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-engine/execute`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ propertyIds }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Pricing engine execution failed: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to execute pricing engine:', error);
      return {
        success: false,
        propertiesProcessed: 0,
        pricesUpdated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      };
    }
  }

  /**
   * Execute WebHotelier-integrated pricing for specific properties
   */
  private async executeWebHotelierIntegratedPricing(
    propertyIds: string[]
  ): Promise<PricingExecutionResult> {
    let totalPropertiesProcessed = 0;
    let totalPricesUpdated = 0;
    const allErrors: string[] = [];
    let overallSuccess = true;

    for (const propertyId of propertyIds) {
      try {
        // Use real-time pricing service for execution
        const realTimePricingService = this.getRealTimePricingService();
        if (!realTimePricingService) {
          throw new Error('Real-time pricing service not available');
        }

        const executionResult = await realTimePricingService.executeNow(
          propertyId,
          {
            lookAheadDays: PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
            minConfidence: PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
            maxPriceChange: PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
            dryRun: false,
          }
        );

        if (executionResult.success) {
          totalPropertiesProcessed += 1;
          totalPricesUpdated += executionResult.data!.ratesUpdated;
          allErrors.push(...executionResult.data!.errors);
        } else {
          overallSuccess = false;
          allErrors.push(
            executionResult.error?.message ||
              `Failed to execute pricing for property ${propertyId}`
          );
        }
      } catch (error) {
        overallSuccess = false;
        allErrors.push(
          `Property ${propertyId}: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }

    return {
      success: overallSuccess && allErrors.length === 0,
      propertiesProcessed: totalPropertiesProcessed,
      pricesUpdated: totalPricesUpdated,
      errors: allErrors,
    };
  }

  /**
   * Preview pricing changes with custom occupancy data
   * Enhanced with WebHotelier integration
   */
  async previewPricing(
    request: PricingPreviewRequest
  ): Promise<PricingPreviewResponse> {
    try {
      await assertPropertyAccess(request.propertyId);
      // Try WebHotelier-integrated preview first
      const previewDate = new Date(request.date);
      const pricingBridge = this.getPricingBridge();

      if (!pricingBridge) {
        throw new Error(
          'Pricing bridge not available - falling back to legacy approach'
        );
      }

      const calculationResult = await pricingBridge.calculatePrices(
        request.propertyId,
        previewDate,
        previewDate,
        request.roomTypeId
      );

      if (calculationResult.success) {
        const calculatedPrices = calculationResult.data!;
        const key = `${request.roomTypeId}-${request.date}`;
        const calculatedPrice = calculatedPrices.get(key);

        if (calculatedPrice) {
          return {
            currentPrice: calculatedPrice.originalPrice,
            suggestedPrice: calculatedPrice.suggestedPrice,
            appliedRules: calculatedPrice.appliedRules,
            priceChange: calculatedPrice.adjustment,
            priceChangePercentage: calculatedPrice.adjustmentPercentage,
          };
        }
      }

      // Fallback to legacy approach
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-engine/preview`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Pricing preview failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to preview pricing:', error);
      throw error;
    }
  }

  /**
   * Handle cancellation repricing
   */
  async handleCancellationRepricing(
    propertyId: string,
    roomTypeId: string,
    checkInDate: string,
    checkOutDate: string
  ): Promise<{ pricesUpdated: number }> {
    try {
      await assertPropertyAccess(propertyId);
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-engine/cancellation`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({
            propertyId,
            roomTypeId,
            checkInDate,
            checkOutDate,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Cancellation repricing failed: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to handle cancellation repricing:', error);
      throw error;
    }
  }

  /**
   * Execute the pricing scheduler manually
   */
  async executeScheduler(): Promise<{
    occupancyFetch: unknown;
    pricingExecution: unknown;
  }> {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-scheduler/execute`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Scheduler execution failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to execute scheduler:', error);
      throw error;
    }
  }

  /**
   * Fetch occupancy data from PMS
   */
  async fetchOccupancyData(): Promise<{
    success: boolean;
    recordsUpdated: number;
    errors: string[];
  }> {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-scheduler/occupancy`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Occupancy fetch failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch occupancy data:', error);
      throw error;
    }
  }

  /**
   * Get pricing rules for a property
   */
  async getPricingRules(propertyId: string): Promise<PricingRule[]> {
    try {
      await assertPropertyAccess(propertyId);
      const { data, error } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('property_id', propertyId)
        .order('priority');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get pricing rules:', error);
      return [];
    }
  }

  /**
   * Alias for getPricingRules for consistency
   */
  async getRules(propertyId: string): Promise<PricingRule[]> {
    return this.getPricingRules(propertyId);
  }

  private computeAffectedDateRange(rule: Record<string, unknown>): {
    start: string;
    end: string;
  } {
    if (rule.valid_from && rule.valid_until) {
      return {
        start: rule.valid_from as string,
        end: rule.valid_until as string,
      };
    }

    const conditions = rule.conditions as Record<string, unknown> | undefined;
    if (conditions) {
      const dateRange = conditions.date_range as
        | {
            start?: string;
            end?: string;
            start_date?: string;
            end_date?: string;
          }
        | undefined;
      if (dateRange) {
        const s = dateRange.start_date || dateRange.start;
        const e = dateRange.end_date || dateRange.end;
        if (s && e) return { start: s, end: e };
      }

      if (conditions.start_date && conditions.end_date) {
        return {
          start: conditions.start_date as string,
          end: conditions.end_date as string,
        };
      }
    }

    const today = new Date().toISOString().split('T')[0];
    const future = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];
    return { start: today, end: future };
  }

  private async writeRuleHistory(
    rule: Record<string, unknown>,
    action: 'created' | 'updated' | 'deleted',
    changeReason: string
  ): Promise<void> {
    try {
      const ruleId = rule.id as string;

      const { data: existingVersions } = await supabase
        .from('pricing_rules_history')
        .select('version')
        .eq('rule_id', ruleId)
        .order('version', { ascending: false })
        .limit(1);

      const nextVersion =
        existingVersions && existingVersions.length > 0
          ? existingVersions[0].version + 1
          : 1;

      const affectedRange = this.computeAffectedDateRange(rule);

      const { error: historyError } = await supabase
        .from('pricing_rules_history')
        .insert({
          rule_id: ruleId,
          version: nextVersion,
          property_id: rule.property_id,
          rule_name: rule.rule_name,
          description: rule.description,
          rule_type: rule.rule_type,
          conditions: rule.conditions,
          actions: rule.actions,
          active: rule.active,
          priority: rule.priority,
          scope: rule.scope,
          room_type_ids: rule.room_type_ids,
          action,
          change_reason: changeReason,
          changed_at: new Date().toISOString(),
          valid_from: rule.valid_from || null,
          valid_until: rule.valid_until || null,
          // Real columns on pricing_rules — a snapshot without them would lose
          // the rule's lead-time range and a revert would widen the rule.
          lead_time_min: rule['lead_time_min'] ?? null,
          lead_time_max: rule['lead_time_max'] ?? null,
          affected_dates_start: affectedRange.start,
          affected_dates_end: affectedRange.end,
        });

      if (historyError) {
        console.warn('Could not save rule history:', historyError.message);
      }
    } catch (err) {
      console.warn('Failed to write rule history:', err);
    }
  }

  /**
   * Create or update a pricing rule
   * Uses INSERT for new rules and UPDATE for existing rules
   */
  async savePricingRule(rule: Partial<PricingRule>): Promise<PricingRule> {
    try {
      if (rule.property_id) {
        await assertPropertyAccess(rule.property_id);
      }
      console.log(
        '[PricingEngineService] Saving rule:',
        JSON.stringify(rule, null, 2)
      );

      // Remove undefined fields, temp IDs, and fields not in DB schema
      const cleanRule = Object.fromEntries(
        Object.entries(rule).filter(([key, v]) => {
          if (v === undefined) return false;
          // Remove temp IDs - database will generate proper UUID
          if (key === 'id' && typeof v === 'string' && v.startsWith('temp-'))
            return false;
          // valid_from/valid_until columns may not exist on pricing_rules table;
          // dates are stored inside conditions.date_range (JSONB) instead
          if (key === 'valid_from' || key === 'valid_until') return false;
          return true;
        })
      );

      const isNewRule = !rule.id || rule.id.startsWith('temp-');
      console.log('[PricingEngineService] Is new rule:', isNewRule);
      console.log(
        '[PricingEngineService] Clean rule:',
        JSON.stringify(cleanRule, null, 2)
      );

      let data;
      let error;

      if (isNewRule) {
        // INSERT new rule
        const result = await supabase
          .from('pricing_rules')
          .insert(cleanRule)
          .select()
          .single();
        data = result.data;
        error = result.error;
      } else {
        // UPDATE existing rule by ID
        const result = await supabase
          .from('pricing_rules')
          .update(cleanRule)
          .eq('id', rule.id!)
          .select()
          .single();
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('[PricingEngineService] Supabase error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log('[PricingEngineService] Saved successfully:', data?.id);

      await this.writeRuleHistory(
        data,
        isNewRule ? 'created' : 'updated',
        isNewRule ? 'Rule created' : 'Rule updated'
      );

      return data;
    } catch (error) {
      console.error('Failed to save pricing rule:', error);
      throw error;
    }
  }

  /**
   * Delete a pricing rule (with history tracking)
   */
  async deletePricingRule(
    ruleId: string,
    changeReason?: string
  ): Promise<void> {
    try {
      // First, fetch the rule to save to history before deletion
      const { data: rule, error: fetchError } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('id', ruleId)
        .single();

      if (fetchError) {
        console.warn('Could not fetch rule for history:', fetchError.message);
      }

      if (rule) {
        await this.writeRuleHistory(
          rule,
          'deleted',
          changeReason || 'Rule deleted'
        );
      }

      // Now delete the rule
      const { error } = await supabase
        .from('pricing_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete pricing rule:', error);
      throw error;
    }
  }

  /**
   * Get room type limits for a property
   */
  async getRoomTypeLimits(propertyId: string): Promise<RoomTypeLimit[]> {
    try {
      await assertPropertyAccess(propertyId);
      const { data, error } = await supabase
        .from('room_type_limits')
        .select('*')
        .eq('property_id', propertyId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get room type limits:', error);
      return [];
    }
  }

  /**
   * Save room type limits
   */
  async saveRoomTypeLimit(
    limit: Partial<RoomTypeLimit>
  ): Promise<RoomTypeLimit> {
    try {
      if (limit.property_id) {
        await assertPropertyAccess(limit.property_id);
      }
      const { data, error } = await supabase
        .from('room_type_limits')
        .upsert(limit)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to save room type limit:', error);
      throw error;
    }
  }

  /**
   * Get price change logs
   */
  async getPriceLogs(
    propertyId: string,
    filters?: {
      roomTypeId?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
    }
  ): Promise<PriceLog[]> {
    try {
      await assertPropertyAccess(propertyId);
      let query = supabase
        .from('price_logs')
        .select('*')
        .eq('property_id', propertyId)
        .order('timestamp', { ascending: false });

      if (filters?.roomTypeId) {
        query = query.eq('room_type_id', filters.roomTypeId);
      }

      if (filters?.dateFrom) {
        query = query.gte('date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('date', filters.dateTo);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get price logs:', error);
      return [];
    }
  }

  /**
   * Get occupancy data for analysis
   */
  async getOccupancyData(
    propertyId: string,
    dateFrom: string,
    dateTo: string
  ): Promise<Record<string, unknown>[]> {
    try {
      await assertPropertyAccess(propertyId);
      const { data, error } = await supabase
        .from('occupancy_data')
        .select('*')
        .eq('property_id', propertyId)
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get occupancy data:', error);
      return [];
    }
  }

  /**
   * Get pricing schedule status
   */
  async getScheduleStatus(): Promise<Record<string, unknown> | null> {
    try {
      const { data, error } = await supabase
        .from('pricing_schedules')
        .select('*')
        .eq('schedule_name', 'Daily Pricing Update');

      if (error) throw error;

      // Return null if no schedule exists, otherwise return the first result
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Failed to get schedule status:', error);
      return null;
    }
  }

  /**
   * Update schedule configuration
   */
  async updateScheduleConfig(config: {
    active?: boolean;
    cron_expression?: string;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('pricing_schedules')
        .update(config)
        .eq('schedule_name', 'Daily Pricing Update');

      if (error) throw error;

      // If activating schedule, start real-time pricing
      if (config.active === true) {
        // Get all properties and start automatic pricing
        const { data: properties } = await supabase
          .from('properties')
          .select('id');

        if (properties) {
          const realTimePricingService = this.getRealTimePricingService();
          if (realTimePricingService) {
            for (const property of properties) {
              await realTimePricingService.startAutomaticPricing(property.id);
            }
          }
        }
      } else if (config.active === false) {
        // Stop automatic pricing for all properties
        const { data: properties } = await supabase
          .from('properties')
          .select('id');

        if (properties) {
          const realTimePricingService = this.getRealTimePricingService();
          if (realTimePricingService) {
            for (const property of properties) {
              realTimePricingService.stopAutomaticPricing(property.id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to update schedule config:', error);
      throw error;
    }
  }

  /**
   * Start automatic pricing for a property
   */
  async startAutomaticPricing(propertyId: string): Promise<void> {
    try {
      await assertPropertyAccess(propertyId);
      const realTimePricingService = this.getRealTimePricingService();
      if (!realTimePricingService) {
        throw new Error('Real-time pricing service not available');
      }

      const result =
        await realTimePricingService.startAutomaticPricing(propertyId);
      if (!result.success) {
        throw new Error(
          result.error?.message || 'Failed to start automatic pricing'
        );
      }
    } catch (error) {
      console.error(
        `Failed to start automatic pricing for property ${propertyId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Stop automatic pricing for a property
   */
  stopAutomaticPricing(propertyId: string): void {
    const realTimePricingService = this.getRealTimePricingService();
    if (realTimePricingService) {
      realTimePricingService.stopAutomaticPricing(propertyId);
    }
  }

  /**
   * Get pricing execution history
   */
  async getPricingExecutionHistory(
    propertyId: string,
    limit?: number
  ): Promise<Record<string, unknown>[]> {
    try {
      const realTimePricingService = this.getRealTimePricingService();
      if (!realTimePricingService) {
        return [];
      }

      const result = await realTimePricingService.getExecutionHistory(
        propertyId,
        limit
      );
      return result.success ? result.data! : [];
    } catch (error) {
      console.error('Failed to get pricing execution history:', error);
      return [];
    }
  }

  /**
   * Get price change notifications
   */
  async getPriceChangeNotifications(
    propertyId: string
  ): Promise<Record<string, unknown>[]> {
    try {
      const realTimePricingService = this.getRealTimePricingService();
      if (!realTimePricingService) {
        return [];
      }

      const result =
        await realTimePricingService.getUnacknowledgedNotifications(propertyId);
      return result.success ? result.data! : [];
    } catch (error) {
      console.error('Failed to get price change notifications:', error);
      return [];
    }
  }

  /**
   * Check if pricing is currently executing for a property
   */
  isPricingExecuting(propertyId: string): boolean {
    const realTimePricingService = this.getRealTimePricingService();
    if (!realTimePricingService) {
      return false;
    }
    return realTimePricingService.isExecuting(propertyId);
  }

  /**
   * Execute pricing engine triggered by a webhook booking event
   * Evaluates all active pricing rules for the affected dates
   */
  async executeOnBookingEvent(params: {
    propertyId: string;
    eventType: 'new' | 'update' | 'cancel' | 'restore';
    bookingId: string;
    roomTypeCode?: string;
    affectedDates: string[];
  }): Promise<PricingExecutionResult> {
    await assertPropertyAccess(params.propertyId);
    console.log(
      `[PricingEngineService] Webhook event trigger: ${params.eventType} for booking ${params.bookingId}`
    );
    console.log(
      `[PricingEngineService] Affected dates: ${params.affectedDates.join(', ')}`
    );

    try {
      // Use real-time pricing service to execute pricing for this property
      const realTimePricingService = this.getRealTimePricingService();
      if (!realTimePricingService) {
        throw new Error('Real-time pricing service not available');
      }

      // Execute pricing with focus on affected dates
      const executionResult = await realTimePricingService.executeNow(
        params.propertyId,
        {
          lookAheadDays: Math.max(
            PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
            params.affectedDates.length + 7
          ),
          minConfidence: PRICING_CONFIG.DEFAULT_MIN_CONFIDENCE,
          maxPriceChange: PRICING_CONFIG.DEFAULT_MAX_PRICE_CHANGE,
          dryRun: false,
        }
      );

      if (executionResult.success) {
        console.log(
          `[PricingEngineService] Webhook-triggered pricing complete: ${executionResult.data?.ratesUpdated} rates updated`
        );

        // Log the execution to pricing_execution_logs. Column names must match
        // the actual table schema (schedule_id/execution_time/duration/status/
        // rooms_processed/errors/results) — a prior version used made-up column
        // names (trigger/description/success/execution_time_ms/metadata), which
        // PostgREST rejects, so every webhook-triggered execution silently
        // failed to log and this audit trail was empty.
        const { error: logInsertError } = await supabase
          .from('pricing_execution_logs')
          .insert({
            schedule_id: null,
            property_id: params.propertyId,
            execution_time: new Date().toISOString(),
            duration: executionResult.data?.executionTime || 0,
            status: 'success',
            rooms_processed: null,
            rates_updated: executionResult.data?.ratesUpdated || 0,
            errors: executionResult.data?.errors || [],
            results: {
              trigger: `webhook_${params.eventType}`,
              description: `Auto-repricing from webhook: ${params.eventType} booking ${params.bookingId}`,
              booking_id: params.bookingId,
              room_type_code: params.roomTypeCode,
              affected_dates: params.affectedDates,
            },
          });
        if (logInsertError) {
          console.warn(
            '[PricingEngineService] Failed to write pricing_execution_logs:',
            logInsertError.message
          );
        }

        return {
          success: true,
          propertiesProcessed: 1,
          pricesUpdated: executionResult.data?.ratesUpdated || 0,
          errors: executionResult.data?.errors || [],
        };
      } else {
        throw new Error(
          executionResult.error?.message || 'Pricing execution failed'
        );
      }
    } catch (error) {
      console.error(
        '[PricingEngineService] Webhook-triggered pricing error:',
        error
      );

      // Log the failed execution (see column-name note above)
      const { error: failedLogInsertError } = await supabase
        .from('pricing_execution_logs')
        .insert({
          schedule_id: null,
          property_id: params.propertyId,
          execution_time: new Date().toISOString(),
          duration: 0,
          status: 'failure',
          rooms_processed: null,
          rates_updated: 0,
          errors: [error instanceof Error ? error.message : 'Unknown error'],
          results: {
            trigger: `webhook_${params.eventType}`,
            description: `Failed auto-repricing from webhook: ${params.eventType} booking ${params.bookingId}`,
            booking_id: params.bookingId,
            room_type_code: params.roomTypeCode,
            affected_dates: params.affectedDates,
          },
        });
      if (failedLogInsertError) {
        console.warn(
          '[PricingEngineService] Failed to write failure to pricing_execution_logs:',
          failedLogInsertError.message
        );
      }

      return {
        success: false,
        propertiesProcessed: 0,
        pricesUpdated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      };
    }
  }

  /**
   * Apply pricing rules for the entire year ahead (365 days)
   * Called when a pricing rule is created or modified
   */
  async applyRulesForYear(propertyId: string): Promise<{
    success: boolean;
    pricesUpdated: number;
    webHotelierUpdated: number;
    daysProcessed: number;
    errors: string[];
  }> {
    await assertPropertyAccess(propertyId);
    console.log(
      `[PricingEngineService] Applying rules for 365 days ahead for property ${propertyId}`
    );
    console.log(`[PricingEngineService] Starting Edge Function call...`);

    // Add timeout to prevent hanging requests (2 minutes should be enough for 365 days)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('[PricingEngineService] Request timeout after 120 seconds');
      controller.abort();
    }, 120000);

    try {
      const startTime = Date.now();
      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/pricing-engine/apply-rules`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({
            propertyId,
            trigger: 'rule_save',
          }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);
      const duration = ((Date.now() - startTime) / 1000).toFixed(2);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `[PricingEngineService] Edge Function failed: ${response.status} - ${errorText}`
        );
        throw new Error(
          `Apply rules failed: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log(
        `[PricingEngineService] ✅ Apply rules completed in ${duration}s:`,
        {
          success: result.success,
          pricesUpdated: result.pricesUpdated,
          webHotelierUpdated: result.webHotelierUpdated,
          daysProcessed: result.daysProcessed,
        }
      );

      return {
        success: result.success,
        pricesUpdated: result.pricesUpdated || 0,
        webHotelierUpdated: result.webHotelierUpdated || 0,
        daysProcessed: result.daysProcessed || 0,
        errors: result.errors || [],
      };
    } catch (error) {
      clearTimeout(timeoutId);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error(
        '[PricingEngineService] ❌ Failed to apply rules for year:',
        errorMessage
      );
      return {
        success: false,
        pricesUpdated: 0,
        webHotelierUpdated: 0,
        daysProcessed: 0,
        errors: [errorMessage],
      };
    }
  }
}

// Export singleton instance
export const pricingEngineService = new PricingEngineService();
